import React from 'react';
import PropTypes from 'prop-types';

import EditIcon from '../../../../Assets/images/svg/dar_pencil_gray_opacity.svg';

const RED = '#ff4d4f';
const AMBER = '#d99a00';
const GREEN = '#22a652';
const API_PLACEHOLDER = '9999-01-01';

const toValidDate = (value) => {
  const raw = String(value || '').trim();
  if (!raw || raw === API_PLACEHOLDER) return null;
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const normalizeProjectType = (value) => String(value || '').trim()
  .toUpperCase();
const normalizeProjectStatus = (value) => String(value || '').trim()
  .toLowerCase();

const getDelayColor = (timeline) => {
  const delayDays = Number(timeline?.delay?.days);
  const baselineEndDate = toValidDate(timeline?.endDateRaw);
  const replannedEndDate = toValidDate(timeline?.replannedEndDateRaw);
  if (!Number.isFinite(delayDays) || !baselineEndDate || !replannedEndDate) return '';

  const projectType = normalizeProjectType(timeline?.projectType);
  if (projectType === 'FP' || projectType === 'FPP') {
    if (delayDays <= 0) return GREEN;
    if (delayDays > 0 && delayDays <= 15) return AMBER;
    return RED;
  }

  if (projectType === 'TM' || projectType === 'T&M') {
    return delayDays <= 0 ? GREEN : RED;
  }

  return '';
};

const getRpedColor = (timeline) => {
  const replannedEndDate = toValidDate(timeline?.replannedEndDateRaw);
  const baselineEndDate = toValidDate(timeline?.endDateRaw);
  if (!replannedEndDate) return '';

  const projectStatus = normalizeProjectStatus(timeline?.projectStatus);
  if (!['in progress', 'on hold'].includes(projectStatus)) return '';

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const nextFiveDays = new Date(today);
  nextFiveDays.setDate(today.getDate() + 5);

  const projectType = normalizeProjectType(timeline?.projectType);
  if ((projectType === 'FP' || projectType === 'FPP') && projectStatus === 'in progress') {
    if (replannedEndDate <= today) return RED;
    if (replannedEndDate > today && replannedEndDate <= nextFiveDays) return AMBER;
    return '';
  }

  if (replannedEndDate > today && replannedEndDate <= nextFiveDays) return AMBER;
  if (baselineEndDate && replannedEndDate.getTime() !== baselineEndDate.getTime()) return RED;
  return '';
};

const TimelineCard = ({ timeline, onEdit }) => {
  const rpedColor = getRpedColor(timeline);
  const delayColor = getDelayColor(timeline);

  return (
    <div className="timeline-card">
      <div className="card-header">
        <h3 className="card-title">Timeline</h3>
        <span className="divider" />
        {onEdit ? (
          <button className="edit-icon" aria-label="Edit" onClick={onEdit}>
            <img src={EditIcon} alt="" />
          </button>
        ) : null}
      </div>
      <div className="timeline-content">
        <div className="timeline-row">
          <div className="timeline-item">
            <span className="label">BLSD:</span>
            <span className="value">{timeline?.rlsd ?? '-'}</span>
          </div>
          <span className="timeline-divider" />
          <div className="timeline-item">
            <span className="label">BLED:</span>
            <span className="value">{timeline?.rled ?? '-'}</span>
          </div>
          <span className="timeline-divider" />
          <div className="timeline-item">
            <span className="label">ACSD:</span>
            <span className="value">{timeline?.acsd ?? '-'}</span>
          </div>
          <span className="timeline-divider" />
          <div className="timeline-item">
            <span className="label">RPED:</span>
            <span className="value" style={rpedColor ? { color: rpedColor } : undefined}>{timeline?.rped ?? '-'}</span>
          </div>
          <span className="timeline-divider" />
          <div className="timeline-item">
            <span className="label">Delay:</span>
            <span className="value" style={delayColor ? { color: delayColor } : undefined}>
              {timeline?.delay
                ? (
                  <>
                    {timeline.delay.days} Days ({timeline.delay.percentage}%)
                  </>
                )
                : '-'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimelineCard;

TimelineCard.propTypes = {
  timeline: PropTypes.shape({
    rlsd: PropTypes.string,
    rled: PropTypes.string,
    acsd: PropTypes.string,
    rped: PropTypes.string,
    startDateRaw: PropTypes.string,
    endDateRaw: PropTypes.string,
    actualStartDateRaw: PropTypes.string,
    replannedEndDateRaw: PropTypes.string,
    projectType: PropTypes.string,
    projectStatus: PropTypes.string,
    delay: PropTypes.shape({
      days: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      percentage: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }),
  }),
  onEdit: PropTypes.func,
};

TimelineCard.defaultProps = {
  timeline: null,
  onEdit: null,
};
